// Custom React hook for managing chat state and streaming

import { useState, useCallback, useRef, useEffect } from 'react';
import type { ChatMessage, ChatState, StreamingEvent, BackendMessage, ToolCall } from '../types/chat';
import { chatApi } from '../services/chatApi';
import { conversationApi } from '../services/conversationApi';

export const useChat = (initialConversationId?: string) => {
  const [state, setState] = useState<ChatState>({
    messages: [],
    isStreaming: false,
    currentStreamingMessage: undefined,
    error: undefined,
    conversationId: initialConversationId,
    messageFeedback: {}
  });

  const streamCleanupRef = useRef<(() => void) | null>(null);

  const generateMessageId = () => `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const generateConversationId = () => {
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    return uuid;
  };

  // Clear conversation when navigating to /conversations/new (no URL param)
  // Conversation loading is handled by ChatInterface to avoid race conditions
  useEffect(() => {
    if (!initialConversationId && state.conversationId) {
      setState(prev => ({
        ...prev,
        conversationId: undefined,
        messages: [],
        currentStreamingMessage: undefined,
        messageFeedback: {}
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConversationId]); // Only depend on URL changes, not state changes

  const addMessage = useCallback((message: Omit<ChatMessage, 'id'>) => {
    const newMessage: ChatMessage = {
      ...message,
      id: generateMessageId()
    };

    setState(prev => ({
      ...prev,
      messages: [...prev.messages, newMessage]
    }));

    return newMessage.id;
  }, []);

  const updateMessage = useCallback((messageId: string, updates: Partial<ChatMessage>) => {
    setState(prev => ({
      ...prev,
      messages: prev.messages.map(msg =>
        msg.id === messageId ? { ...msg, ...updates } : msg
      ),
      currentStreamingMessage: prev.currentStreamingMessage?.id === messageId
        ? { ...prev.currentStreamingMessage, ...updates }
        : prev.currentStreamingMessage
    }));
  }, []);

  const clearMessages = useCallback(() => {
    setState(prev => ({
      ...prev,
      messages: [],
      currentStreamingMessage: undefined,
      error: undefined,
      conversationId: undefined,
      messageFeedback: {}
    }));
  }, []);


  const setConversationId = useCallback(async (conversationId: string) => {
    setState(prev => ({
      ...prev,
      conversationId,
      currentStreamingMessage: undefined
    }));

    try {
      // Load conversation messages and feedback from backend in parallel
      const [messages, feedback] = await Promise.all([
        conversationApi.getConversationMessages(conversationId),
        chatApi.getConversationFeedback(conversationId)
      ]);

      // Convert backend messages to frontend format
      const convertedMessages: ChatMessage[] = messages.map((msg: BackendMessage) => ({
        id: msg.id || `msg-${Date.now()}-${Math.random()}`,
        type: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content,
        timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
        isComplete: msg.isComplete ?? true,
      }));

      setState(prev => ({
        ...prev,
        conversationId,
        messages: convertedMessages,
        currentStreamingMessage: undefined,
        messageFeedback: feedback
      }));

    } catch (error) {
      console.error('Failed to load conversation history:', error);

      // Only show loading message for errors (conversation doesn't exist or auth issues)
      setState(prev => ({
        ...prev,
        conversationId,
        messages: [
          {
            id: 'conversation-error',
            type: 'system',
            content: 'Unable to load conversation. Please try again or start a new chat.',
            timestamp: new Date(),
            isComplete: true
          }
        ],
        currentStreamingMessage: undefined,
        messageFeedback: {}
      }));
      // Re-throw the error so it can be caught by the caller
      throw error;
    }
  }, []);

  const sendMessage = useCallback(async (question: string) => {
    if (!question.trim() || state.isStreaming) return;

    // Generate conversation ID if none exists (for new chats)
    let currentConversationId = state.conversationId;
    if (!currentConversationId) {
      currentConversationId = generateConversationId();
      setState(prev => ({ ...prev, conversationId: currentConversationId }));
    }

    // Clear any previous errors
    setState(prev => ({ ...prev, error: undefined }));

    // Add user message
    addMessage({
      type: 'user',
      content: question.trim(),
      timestamp: new Date(),
      isComplete: true
    });

    // Create initial assistant message for streaming
    const assistantMessageId = addMessage({
      type: 'assistant',
      content: '',
      timestamp: new Date(),
      isComplete: false
    });

    setState(prev => ({
      ...prev,
      isStreaming: true,
      currentStreamingMessage: prev.messages.find(m => m.id === assistantMessageId)
    }));

    let currentContent = '';

    const cleanup = chatApi.streamMessage(
      { question: question.trim(), conversationId: currentConversationId },
      (event: StreamingEvent) => {
        switch (event.event) {
          case 'status': {
            // Update or create a single status message that gets rewritten with each new status
            const statusContent = event.data as string;

            setState(prev => {
              const newMessages = [...prev.messages];
              const assistantIndex = newMessages.findIndex(m => m.id === assistantMessageId);

              if (assistantIndex !== -1) {
                // Look for existing status message right before the assistant message
                const statusIndex = assistantIndex > 0 && newMessages[assistantIndex - 1].type === 'status'
                  ? assistantIndex - 1
                  : -1;

                if (statusIndex !== -1) {
                  // Update existing status message
                  newMessages[statusIndex] = {
                    ...newMessages[statusIndex],
                    content: statusContent,
                    timestamp: new Date()
                  };
                } else {
                  // Create new status message before assistant message
                  const statusMessage: ChatMessage = {
                    id: generateMessageId(),
                    type: 'status',
                    content: statusContent,
                    timestamp: new Date(),
                    isComplete: true
                  };
                  newMessages.splice(assistantIndex, 0, statusMessage);
                }
              }

              return {
                ...prev,
                messages: newMessages
              };
            });
            break;
          }

          case 'tool-call': {
            // Insert tool call message before the current streaming assistant message
            const toolCallMessage: ChatMessage = {
              id: generateMessageId(),
              type: 'tool-call',
              content: event.data as string,
              timestamp: new Date(),
              isComplete: true
            };

            setState(prev => {
              // Find the index of the current streaming assistant message
              const assistantIndex = prev.messages.findIndex(m => m.id === assistantMessageId);

              if (assistantIndex !== -1) {
                // Insert the tool call message right before the assistant message
                const newMessages = [...prev.messages];
                newMessages.splice(assistantIndex, 0, toolCallMessage);
                return {
                  ...prev,
                  messages: newMessages
                };
              } else {
                // Fallback: append if we can't find the assistant message
                return {
                  ...prev,
                  messages: [...prev.messages, toolCallMessage]
                };
              }
            });
            break;
          }

          case 'tool-calls': {
            // Received tool calls array for admin users
            const toolCalls = event.data as ToolCall[];
            updateMessage(assistantMessageId, {
              toolCalls
            });
            break;
          }

          case 'suggestions': {
            // Received suggestions array from completion service
            const suggestions = event.data as string[];
            updateMessage(assistantMessageId, {
              suggestions
            });
            break;
          }

          case 'chunk':
            // Append to current streaming message
            currentContent += event.data as string;
            updateMessage(assistantMessageId, {
              content: currentContent
            });

            // Remove status message when assistant starts responding (first chunk only)
            if (currentContent.length === (event.data as string).length) {
              // This is the first chunk - remove status message
              setState(prev => {
                const newMessages = [...prev.messages];
                const assistantIndex = newMessages.findIndex(m => m.id === assistantMessageId);

                if (assistantIndex > 0 && newMessages[assistantIndex - 1].type === 'status') {
                  // Remove the status message
                  newMessages.splice(assistantIndex - 1, 1);
                }

                return {
                  ...prev,
                  messages: newMessages
                };
              });
            }
            break;

          case 'complete':
            updateMessage(assistantMessageId, {
              isComplete: true
            });
            setState(prev => ({
              ...prev,
              isStreaming: false,
              currentStreamingMessage: undefined
            }));
            break;

          case 'error': {
            const errorMessage = event.data as string;
            addMessage({
              type: 'error',
              content: errorMessage,
              timestamp: new Date(),
              isComplete: true
            });
            setState(prev => ({
              ...prev,
              isStreaming: false,
              currentStreamingMessage: undefined,
              // Set error state for message limit errors to disable input
              error: errorMessage.includes('Message limit reached') ? errorMessage : prev.error
            }));
            break;
          }

          case 'grace-period': {
            const gracePeriodMessage = event.data as string;
            addMessage({
              type: 'system',
              content: gracePeriodMessage,
              timestamp: new Date(),
              isComplete: true
            });
            setState(prev => ({
              ...prev,
              isStreaming: false,
              currentStreamingMessage: undefined
            }));
            break;
          }
        }
      },
      (error: Error) => {
        console.error('Streaming error:', error);
        setState(prev => {
          // Don't overwrite message limit errors with connection errors
          const shouldPreserveError = prev.error && prev.error.includes('Message limit reached');

          // Don't add connection errors to chat if we already have a message limit error
          if (!shouldPreserveError && !error.message.includes('Connection to server lost')) {
            addMessage({
              type: 'error',
              content: `Connection error: ${error.message}`,
              timestamp: new Date(),
              isComplete: true
            });
          }

          return {
            ...prev,
            isStreaming: false,
            currentStreamingMessage: undefined,
            error: shouldPreserveError ? prev.error : error.message
          };
        });
      },
      () => {
        // Stream completed
        setState(prev => ({
          ...prev,
          isStreaming: false,
          currentStreamingMessage: undefined
        }));
      }
    );

    streamCleanupRef.current = cleanup;
  }, [state.isStreaming, state.conversationId, addMessage, updateMessage]);

  const stopStreaming = useCallback(() => {
    if (streamCleanupRef.current) {
      streamCleanupRef.current();
      streamCleanupRef.current = null;
    }
    setState(prev => ({
      ...prev,
      isStreaming: false,
      currentStreamingMessage: undefined
    }));
  }, []);

  const sendMessageNonStreaming = useCallback(async (question: string) => {
    if (!question.trim() || state.isStreaming) return;

    // Generate conversation ID if none exists (for new chats)
    let currentConversationId = state.conversationId;
    if (!currentConversationId) {
      currentConversationId = generateConversationId();
      setState(prev => ({ ...prev, conversationId: currentConversationId }));
    }

    setState(prev => ({ ...prev, error: undefined }));

    // Add user message
    addMessage({
      type: 'user',
      content: question.trim(),
      timestamp: new Date(),
      isComplete: true
    });

    // Add loading message
    const loadingMessageId = addMessage({
      type: 'assistant',
      content: '',
      timestamp: new Date(),
      isComplete: false
    });

    try {
      setState(prev => ({ ...prev, isStreaming: true }));

      const response = await chatApi.sendMessage({ question: question.trim(), conversationId: currentConversationId });

      // Update the loading message with the response
      updateMessage(loadingMessageId, {
        content: response.answer,
        isComplete: true
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      updateMessage(loadingMessageId, {
        type: 'error',
        content: `Error: ${errorMessage}`,
        isComplete: true
      });
      setState(prev => ({ ...prev, error: errorMessage }));
    } finally {
      setState(prev => ({ ...prev, isStreaming: false }));
    }
  }, [state.isStreaming, state.conversationId, addMessage, updateMessage]);

  return {
    ...state,
    sendMessage,
    sendMessageNonStreaming,
    stopStreaming,
    clearMessages,
    setConversationId,
    addMessage,
    updateMessage
  };
};
